import TimezoneResolver from "shared/timezone_resolver"
import { init, GlobalHandlers } from "@sentry/react"
import { ExtraErrorData } from "@sentry/integrations"
// datadogRum has side effects that don't work in ExecJS
import { datadogRum } from "@datadog/browser-rum"

import purify from "dompurify"

const errorsToIgnore = [
  "YsAppBridge is not defined", // Ignored as part of legacy page that causes wierd errors: https://ifttt-dev.atlassian.net/browse/OC-1420
  "MtPopUpList is not defined",
  "GEOCODER_GEOCODE: ZERO_RESULTS", // Difficult-to-catch error from Google Geocoder API
]

const interceptUpgradeFromMobileUsers = event => {
  if (!event.target.closest("header.unified-header.signed-in a[data-intercept-mobile-users]")) {
    return
  }

  event.preventDefault()
  const paymentType = event.target.dataset["interceptMobileUsers"]

  if (paymentType === "apple") {
    window.appendFlash(
      "You currently have a Pro subscription on Apple. Please upgrade to Pro+ on your iOS device.",
      "danger"
    )
  } else if (paymentType === "google") {
    window.appendFlash(
      "You currently have a Pro subscription on Google. Please upgrade to Pro+ on your Android device.",
      "danger"
    )
  }
}

export function oneTimeSetup() {
  document.addEventListener("click", interceptUpgradeFromMobileUsers)

  TimezoneResolver()
}

const shouldDiscard = event => {
  if (
    event.type === "error" &&
    (event.view?.url?.includes("/join") || event.view?.url?.includes("/login")) &&
    event.error?.message?.includes("csp_violation")
  ) {
    return true
  }

  if (event.type === "error" && errorsToIgnore.some(error => event.error?.message?.includes(error))) {
    return true
  }

  return false
}

export function sharedSetup() {
  window.appendFlash = function (message, type = "success", callback) {
    let msg = document.createElement("div")
    msg.className = `flash-msg alert alert-${type} fadein`
    msg.append(purify.sanitize(message))

    document.querySelector("#flash-messages").appendChild(msg)

    setTimeout(() => {
      msg.classList.remove("fadein")
      msg.classList.add("fadeout")
      setTimeout(() => {
        msg.style.display = "none"
      }, 500)
      callback?.()
    }, 3000)
  }

  document.querySelectorAll(".flash-msg:not(.sticky)").forEach(el => {
    setInterval(function () {
      el.classList.add("fadeout")
      setTimeout(() => {
        el.style.display = "none"
      }, 500)
    }, 3000)
  })

  // Web isn't using experiments for now. Reducing unnecessary calls until we
  // move Experiments to Graph and reimplement the solution.
  // confirmExperimentReceipts()

  // Enable JS error reporting via Sentry
  if (window.App?.sentryDSNGrizzlyClient) {
    init({
      dsn: window.App.sentryDSNGrizzlyClient,
      // With this integration, Sentry will inject enumerable error properties
      // for every JS Object that errs, including non-Error ones.
      integrations: [
        // Most Promise libraries have a global hook for capturing unhandled errors.
        // As we don't have control over what third-party libraries use, we'll
        // turn this kind of error off.
        new GlobalHandlers({ onunhandledrejection: false }),
        new ExtraErrorData(),
      ],
      ignoreErrors: errorsToIgnore,
    })
  }

  if (window.App?.datadogClientToken && window.App.trackSession) {
    datadogRum.init({
      applicationId: window.App.datadogApplicationId,
      clientToken: window.App.datadogClientToken,
      site: "datadoghq.com",
      service: window.App.datadogService,

      // Specify a version number to identify the deployed version of your application in Datadog
      version: window.App.datadogVersion,
      sessionSampleRate: Number(window.App.datadogSessionSampleRate),
      sessionReplaySampleRate: Number(window.App.datadogSessionReplaySampleRate),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend: event => {
        if (shouldDiscard(event)) {
          return false
        }
      },
    })

    datadogRum.startSessionReplayRecording()
    if (window.App.user) {
      let user = window.App.user
      if (!user.id) {
        user.id = window.App.browserSessionId
      }
      if (!user.tier) {
        user.tier = "free"
      }
      datadogRum.setUser(window.App.user)
    }
  }
}

export function logAppletEnable(appletId) {
  const userOnboardingBucket = sessionStorage.getItem("userOnboardingBucket")
  sessionStorage.removeItem("userOnboardingBucket")

  logCustomDatadogAction("applet_enabled", {
    applet_id: appletId,
    is_in_new_user_intake_flow: userOnboardingBucket === "newUserIntake",
  })
}

export function logCustomDatadogAction(actionName, attributes) {
  datadogRum.addAction(actionName, attributes)
}
